/**
 * StripeConnectCallback - Handles OAuth callback from Stripe Connect V2
 * 
 * NOTE: V2 API uses account links instead of OAuth flows.
 * This page now handles the success/error states from the redirect.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

const StripeConnectCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      const stripeStatus = searchParams.get('stripe');
      const accountId = searchParams.get('accountId');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Handle user cancellation or errors
      if (errorParam) {
        setStatus('error');
        setMessage(errorDescription || `Stripe Connect signup cancelled: ${errorParam}`);
        setTimeout(() => navigate('/settings?tab=payments'), 3000);
        return;
      }

      if (stripeStatus === 'success') {
        setStatus('success');
        setMessage('Your Stripe account has been connected successfully!');
        
        // Redirect to payments settings after 2 seconds
        setTimeout(() => {
          navigate('/settings?tab=payments');
        }, 2000);
      } else if (stripeStatus === 'refresh') {
        setStatus('error');
        setMessage('Please complete the Stripe onboarding process to accept payments.');
        setTimeout(() => {
          navigate('/settings?tab=payments');
        }, 3000);
      } else {
        setStatus('error');
        setMessage('An unexpected error occurred during Stripe Connect.');
        setTimeout(() => {
          navigate('/settings?tab=payments');
        }, 3000);
      }
    } catch (err) {
      console.error('Error in callback:', err);
      setStatus('error');
      setMessage(err.message || 'Failed to complete Stripe Connect');
      setTimeout(() => navigate('/settings?tab=payments'), 3000);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="text-center max-w-md mx-auto px-4">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-white mb-2">Connecting Stripe Account...</h2>
            <p className="text-white/70">Please wait while we verify your Stripe Connect account</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-12 h-12 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-green-400 mb-2">Success!</h2>
            <p className="text-white/70">{message}</p>
            <p className="text-white/50 text-sm mt-4">Redirecting to settings...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <ExclamationCircleIcon className="w-12 h-12 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-red-400 mb-2">Connection Issue</h2>
            <p className="text-white/70">{message}</p>
            <p className="text-white/50 text-sm mt-4">Redirecting to settings...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default StripeConnectCallback;
